import {
  Box,
  Flex,
  Heading,
  Button,
  Card,
  CardBody,
  useColorModeValue,
  Icon,
  Stack,
  Checkbox,
  HStack,
  Badge
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { ListingDataSuggestionModel } from 'models';
import { ListingAccordion } from 'components';
import { MdArrowForward, MdCheck, MdClose } from 'react-icons/md';
import { NavigationService } from 'services';

export interface ListingDataSuggestionCardProps {
  suggestion: ListingDataSuggestionModel;
  onApprove?: (suggestionId: string) => void;
  onReject?: (suggestionId: string) => void;
  isLoading?: boolean;
  compact?: boolean;
  selectionMode?: boolean;
  selected?: boolean;
  onSelect?: () => void;
}

export default function ListingDataSuggestionCard({
  suggestion,
  onApprove,
  onReject,
  isLoading = false,
  compact = false,
  selectionMode = false,
  selected = false,
  onSelect
}: ListingDataSuggestionCardProps) {
  const navigate = useNavigate();
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const selectedBgColor = useColorModeValue('blue.50', 'blue.900');
  const selectedBorderColor = useColorModeValue('blue.400', 'blue.500');
  const hoverBgColor = useColorModeValue('gray.50', 'gray.700');

  const handleListingClick = (id: string, name: string) => {
    navigate(NavigationService.getListingDetailsPath(id, name));
  };

  const handleArtistClick = (artistId: string, artistName: string) => {
    navigate(NavigationService.getArtistDetailsPath(artistId, artistName));
  };

  const handleApprove = () => {
    onApprove?.(suggestion.id);
  };

  const handleReject = () => {
    onReject?.(suggestion.id);
  };

  // Get the updated listing with suggested changes applied
  const updatedListing = suggestion.getUpdatedListing();

  return (
    <Card
      variant="outline"
      borderColor={selected ? selectedBorderColor : borderColor}
      borderWidth={selected ? '2px' : '1px'}
      bg={selected ? selectedBgColor : undefined}
      cursor={selectionMode ? 'pointer' : 'default'}
      onClick={() => {
        if (selectionMode && onSelect) {
          onSelect();
        }
      }}
      _hover={selectionMode ? { bg: selected ? selectedBgColor : hoverBgColor } : undefined}
      transition="all 0.2s"
    >
      <CardBody>
        <Stack spacing={4}>
          <HStack spacing={3}>
            {selectionMode && (
              <Checkbox
                isChecked={selected}
                onChange={(e) => {
                  e.stopPropagation();
                  onSelect?.();
                }}
                size="lg"
                colorScheme="blue"
              />
            )}
            <Badge colorScheme={suggestion.isPending() ? 'yellow' : suggestion.isApproved() ? 'green' : 'red'}>
              {suggestion.status}
            </Badge>
          </HStack>
          <Flex direction={compact ? 'column' : { base: 'column', lg: 'row' }} gap={4} alignItems="flex-start">
            {/* Original Listing */}
            <Box flex={1} width="100%">
              <Heading size="xs" mb={2} color="gray.600">
                Current
              </Heading>
              <ListingAccordion
                listing={suggestion.listing}
                onListingClick={handleListingClick}
                onArtistClick={handleArtistClick}
                showAssociatedItem={false}
                showAssociationAction={false}
                compactMode={true}
              />
            </Box>

            {/* Arrow */}
            <Flex alignItems="center" justifyContent="center" minWidth="40px">
              <Icon as={MdArrowForward} w={6} h={6} color="gray.400" />
            </Flex>

            {/* Proposed Changes */}
            <Box flex={1} width="100%">
              <Heading size="xs" mb={2} color="blue.600">
                Proposed
              </Heading>
              <ListingAccordion
                listing={updatedListing}
                onListingClick={handleListingClick}
                onArtistClick={handleArtistClick}
                showAssociatedItem={false}
                showAssociationAction={false}
                compactMode={true}
              />
            </Box>
          </Flex>

          {/* Action Buttons */}
          {!selectionMode && suggestion.isPending() && (
            <Flex gap={2} justifyContent="flex-end">
              <Button
                leftIcon={<MdClose />}
                size="sm"
                colorScheme="red"
                variant="outline"
                onClick={handleReject}
                isDisabled={isLoading}
              >
                Reject
              </Button>
              <Button
                leftIcon={<MdCheck />}
                size="sm"
                colorScheme="green"
                onClick={handleApprove}
                isDisabled={isLoading}
              >
                Approve
              </Button>
            </Flex>
          )}
        </Stack>
      </CardBody>
    </Card>
  );
}
